import axios from "../../utils/axios";
import { Formik } from "formik";
import {
  Alert as MuiAlert,
  Box,
  Button as MuiButton,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { spacing, SpacingProps } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useCustomerPaymentInfo } from "./index";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormikHandlers, FormikHelpers } from "formik/dist/types";
import { useAcceptOrderFromPreviousOrder } from "../../api/Api";
import { toDecimal } from "dinero.js";
import { dineroAUDFromFloat } from "../../utils/dineroAUDFromFloat";
import { parseBoolean } from "../../utils/parseBoolean";
import { paymentGateway } from "../../config";
import {
  ConfirmOrderArrowIcon,
  formatAudAmount,
  resolveIsCompanyOneSolomons,
  OneSolomonAgreement,
  OneSolomonAlert,
  OneSolomonCard,
  OneSolomonCheckboxGroup,
  OneSolomonCheckboxInput,
  OneSolomonCheckboxLabel,
  OneSolomonConfirmButton,
  OneSolomonDisclaimer,
  OneSolomonField,
  OneSolomonFieldError,
  OneSolomonFieldLabel,
  OneSolomonFieldRow,
  OneSolomonFonts,
  OneSolomonPageBackground,
  OneSolomonForm,
  OneSolomonHeading,
  OneSolomonInput,
  OneSolomonLogo,
  OneSolomonShell,
  OneSolomonSpinner,
  OneSolomonSubheading,
} from "./oneSolomonUi";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);
interface ButtonProps extends SpacingProps {
  component?: string;
}

const Button = styled(MuiButton)<ButtonProps>(spacing);

export type CustomerInfoType = {
  firstName: string;
  lastName: string;
  email: string;
  merchantReference: string; // order reference_number
  totalQuoteAmount: number;
  amount?: number;
  paymentMethod: "credit-card";
  order_uuid?: string; // optional since there are orders not coming from AERP
  transaction_for?: string;
  company_image?: string;
  company_uuid?: string;
  is_previous_order_update?: boolean;
  is_new_order?: boolean;
  isCompanyOneSolomons?: boolean;
};

const validationSchema = Yup.object({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  email: Yup.string().email().required().max(250),
  merchantReference: Yup.string().required("Required").max(50),
  totalQuoteAmount: Yup.number().required("Required").min(1),
  amount: Yup.number().optional(),
  paymentMethod: Yup.string().oneOf(["credit-card"]).required(),
  order_uuid: Yup.string().uuid().optional(),
  company_image: Yup.string().url().optional(),
  company_uuid: Yup.string().uuid().optional(),
  is_previous_order_update: Yup.boolean().optional(),
  is_new_order: Yup.boolean().optional(),
  isCompanyOneSolomons: Yup.boolean().optional(),
  transaction_for: Yup.string()
    .optional()
    .oneOf(["deposit", "pre-delivery", "final"]),
});

type FormContentProps = {
  errors: any;
  handleBlur: FormikHandlers["handleBlur"];
  handleChange: FormikHandlers["handleChange"];
  handleSubmit: FormikHandlers["handleSubmit"];
  isSubmitting: boolean;
  touched: any;
  values: CustomerInfoType;
  status: any;
  setFieldValue: (field: string, value: unknown) => void;
  transactionForDeposit: boolean;
  isCompanyOneSolomons: boolean;
  initialValuesFromMain?: Partial<CustomerInfoType>;
};

const DefaultCustomerInfoFormContent = ({
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  isSubmitting,
  touched,
  values,
  status,
  setFieldValue,
  transactionForDeposit,
  initialValuesFromMain,
}: FormContentProps) => (
  <>
    {transactionForDeposit && (
      <>
        <Typography component="h1" variant="h1" align="center" mt={3}>
          We're almost there!
        </Typography>

        <Typography variant="h5" align="center" mt={5}>
          Are you ready to add that 'wow' factor to your space?
        </Typography>
      </>
    )}

    <CardContent>
      {errors && errors.submit && (
        <Alert severity="error" my={3}>
          {errors.submit}
        </Alert>
      )}

      {status && status.sent && (
        <Alert severity="success" my={3}>
          Your data has been submitted successfully!
        </Alert>
      )}

      {isSubmitting ? (
        <Box display="flex" justifyContent="center" my={6}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={6}>
            <Grid size={6}>
              <TextField
                name="firstName"
                label="First Name"
                value={values.firstName}
                error={Boolean(touched.firstName && errors.firstName)}
                fullWidth
                helperText={touched.firstName && errors.firstName}
                onBlur={handleBlur}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                name="lastName"
                label="Last Name"
                value={values.lastName}
                error={Boolean(touched.lastName && errors.lastName)}
                fullWidth
                helperText={touched.lastName && errors.lastName}
                onBlur={handleBlur}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
              />
            </Grid>
          </Grid>

          <TextField
            name="email"
            label="Email"
            value={values.email}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            variant="outlined"
            margin="normal"
          />
          {transactionForDeposit && (
            <Typography variant="body2" gutterBottom>
              “I have read and agree to the terms and conditions contained within
              and authorise the work to go ahead as outlined on the order number
              below for the specified amount”
            </Typography>
          )}

          <TextField
            name="merchantReference"
            label="Quote Number"
            value={values.merchantReference}
            error={Boolean(
              touched.merchantReference && errors.merchantReference
            )}
            fullWidth
            helperText={touched.merchantReference && errors.merchantReference}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={!!initialValuesFromMain?.merchantReference}
            type="merchantReference"
            variant="outlined"
            margin="normal"
          />

          {transactionForDeposit && (
            <>
              <TextField
                name="totalQuoteAmount"
                label="Total Quote Amount"
                value={toDecimal(
                  dineroAUDFromFloat(Number(values.totalQuoteAmount) || 0)
                )}
                placeholder="$AUD"
                disabled={!!initialValuesFromMain?.totalQuoteAmount}
                error={Boolean(
                  touched.totalQuoteAmount && errors.totalQuoteAmount
                )}
                fullWidth
                helperText={
                  touched.totalQuoteAmount && errors.totalQuoteAmount
                }
                onBlur={handleBlur}
                onChange={handleChange}
                type="number"
                variant="outlined"
                margin="normal"
              />

              <Typography variant="body1" align="justify" my={3}>
                All orders are subject to check measure. You are responsible for
                organising any electrical work. You accept full responsibility for
                colour and fabric selection. No changes can be made after the
                order is placed.
              </Typography>
            </>
          )}

          {transactionForDeposit && (
            <FormControl
              error={Boolean(touched.is_new_order && errors.is_new_order)}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={parseBoolean(values.is_new_order)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setFieldValue("is_previous_order_update", false);
                        }
                        handleChange(event);
                      }}
                      name="is_new_order"
                    />
                  }
                  label="This is a new order"
                />
              </FormGroup>
            </FormControl>
          )}

          {transactionForDeposit && (
            <FormControl
              error={Boolean(
                touched.is_previous_order_update &&
                  errors.is_previous_order_update
              )}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={parseBoolean(values.is_previous_order_update)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setFieldValue("is_new_order", false);
                        }
                        handleChange(event);
                      }}
                      name="is_previous_order_update"
                    />
                  }
                  label="This is an update to a previous order"
                />
              </FormGroup>
            </FormControl>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            margin="normal"
            mt={3}
          >
            {transactionForDeposit ? "Confirm Order" : "Confirm"}
          </Button>
        </form>
      )}
    </CardContent>
  </>
);

const OneSolomonCustomerInfoFormContent = ({
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  isSubmitting,
  touched,
  values,
  status,
  setFieldValue,
  transactionForDeposit,
  initialValuesFromMain,
}: FormContentProps) => {
  const quoteReadOnly = !!initialValuesFromMain?.merchantReference;
  const totalReadOnly = !!initialValuesFromMain?.totalQuoteAmount;

  return (
    <OneSolomonShell>
      <OneSolomonFonts />
      <OneSolomonCard>
        <OneSolomonLogo />

        {transactionForDeposit && (
          <>
            <OneSolomonHeading>We&apos;re almost there.</OneSolomonHeading>
            <OneSolomonSubheading>
              Are you ready to add that &apos;wow&apos; factor to your space?
            </OneSolomonSubheading>
          </>
        )}

        {errors?.submit && (
          <OneSolomonAlert variant="error">{errors.submit}</OneSolomonAlert>
        )}

        {status?.sent && (
          <OneSolomonAlert variant="success">
            Your data has been submitted successfully!
          </OneSolomonAlert>
        )}

        {isSubmitting ? (
          <OneSolomonSpinner />
        ) : (
          <OneSolomonForm onSubmit={handleSubmit} noValidate>
            <OneSolomonFieldRow>
              <OneSolomonField inRow>
                <OneSolomonFieldLabel htmlFor="firstName">
                  First name
                </OneSolomonFieldLabel>
                <OneSolomonInput
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={values.firstName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  hasError={Boolean(touched.firstName && errors.firstName)}
                />
                {touched.firstName && errors.firstName && (
                  <OneSolomonFieldError>{errors.firstName}</OneSolomonFieldError>
                )}
              </OneSolomonField>

              <OneSolomonField inRow>
                <OneSolomonFieldLabel htmlFor="lastName">
                  Last name
                </OneSolomonFieldLabel>
                <OneSolomonInput
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={values.lastName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  hasError={Boolean(touched.lastName && errors.lastName)}
                />
                {touched.lastName && errors.lastName && (
                  <OneSolomonFieldError>{errors.lastName}</OneSolomonFieldError>
                )}
              </OneSolomonField>
            </OneSolomonFieldRow>

            <OneSolomonField>
              <OneSolomonFieldLabel htmlFor="email">Email</OneSolomonFieldLabel>
              <OneSolomonInput
                id="email"
                name="email"
                type="email"
                value={values.email}
                onBlur={handleBlur}
                onChange={handleChange}
                hasError={Boolean(touched.email && errors.email)}
              />
              {touched.email && errors.email && (
                <OneSolomonFieldError>{errors.email}</OneSolomonFieldError>
              )}
            </OneSolomonField>

            {transactionForDeposit && (
              <OneSolomonAgreement>
                &ldquo;I have read and agree to the terms and conditions
                contained within and authorise the work to go ahead as outlined
                on the order number below for the specified amount.&rdquo;
              </OneSolomonAgreement>
            )}

            <OneSolomonField>
              <OneSolomonFieldLabel htmlFor="merchantReference">
                Quote number
              </OneSolomonFieldLabel>
              <OneSolomonInput
                id="merchantReference"
                name="merchantReference"
                type="text"
                value={values.merchantReference}
                onBlur={handleBlur}
                onChange={handleChange}
                readOnly={quoteReadOnly}
                hasError={Boolean(
                  touched.merchantReference && errors.merchantReference
                )}
              />
              {touched.merchantReference && errors.merchantReference && (
                <OneSolomonFieldError>
                  {errors.merchantReference}
                </OneSolomonFieldError>
              )}
            </OneSolomonField>

            {transactionForDeposit && (
              <OneSolomonField>
                <OneSolomonFieldLabel htmlFor="totalQuoteAmount">
                  Total quote amount
                </OneSolomonFieldLabel>
                <OneSolomonInput
                  id="totalQuoteAmount"
                  name="totalQuoteAmount"
                  type={totalReadOnly ? "text" : "number"}
                  value={
                    totalReadOnly
                      ? formatAudAmount(Number(values.totalQuoteAmount) || 0)
                      : toDecimal(
                          dineroAUDFromFloat(
                            Number(values.totalQuoteAmount) || 0
                          )
                        )
                  }
                  readOnly={totalReadOnly}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  hasError={Boolean(
                    touched.totalQuoteAmount && errors.totalQuoteAmount
                  )}
                />
                {touched.totalQuoteAmount && errors.totalQuoteAmount && (
                  <OneSolomonFieldError>
                    {errors.totalQuoteAmount}
                  </OneSolomonFieldError>
                )}
              </OneSolomonField>
            )}

            {transactionForDeposit && (
              <OneSolomonDisclaimer>
                All orders are subject to check measure. You are responsible for
                organising any electrical work. You accept full responsibility
                for colour and fabric selection. No changes can be made after the
                order is placed.
              </OneSolomonDisclaimer>
            )}

            {transactionForDeposit && (
              <OneSolomonCheckboxGroup>
                <OneSolomonCheckboxLabel>
                  <OneSolomonCheckboxInput
                    type="checkbox"
                    name="is_new_order"
                    checked={parseBoolean(values.is_new_order)}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setFieldValue("is_previous_order_update", false);
                      }
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                  />
                  <span>This is a new order</span>
                </OneSolomonCheckboxLabel>

                <OneSolomonCheckboxLabel>
                  <OneSolomonCheckboxInput
                    type="checkbox"
                    name="is_previous_order_update"
                    checked={parseBoolean(values.is_previous_order_update)}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setFieldValue("is_new_order", false);
                      }
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                  />
                  <span>This is an update to a previous order</span>
                </OneSolomonCheckboxLabel>
              </OneSolomonCheckboxGroup>
            )}

            <OneSolomonConfirmButton type="submit">
              <span>
                {transactionForDeposit ? "Confirm order" : "Confirm"}
              </span>
              <ConfirmOrderArrowIcon />
            </OneSolomonConfirmButton>
          </OneSolomonForm>
        )}
      </OneSolomonCard>
    </OneSolomonShell>
  );
};

const CustomerInfoForm = () => {
  const navigate = useNavigate();
  const { values, setValues, initialValuesFromMain } = useCustomerPaymentInfo();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutateAsync: acceptOrderFromPrev } =
    useAcceptOrderFromPreviousOrder();

  const initialValues: CustomerInfoType = {
    firstName: "",
    lastName: "",
    email: "",
    merchantReference: "",
    paymentMethod: "credit-card",
    totalQuoteAmount: 0,
    amount: 0,
    transaction_for: "deposit",
    is_previous_order_update: false,
    is_new_order: true,
    ...values,
  };

  const transactionForDeposit = initialValues?.transaction_for === "deposit";
  const isCompanyOneSolomons = resolveIsCompanyOneSolomons(
    values?.company_uuid ?? initialValuesFromMain?.company_uuid,
    values?.isCompanyOneSolomons,
    initialValuesFromMain?.isCompanyOneSolomons
  );

  const handleSubmit = async (
    values: CustomerInfoType,
    helpers: FormikHelpers<any>
  ) => {
    const valuesStripped = validationSchema.cast(values, {
      stripUnknown: true,
    });

    if (valuesStripped.is_previous_order_update) {
      await handleSubmitPreviousOrderUpdate(valuesStripped);
    } else {
      await handleSubmitCreditCard(valuesStripped, helpers);
    }
  };

  const handleSubmitCreditCard = async (
    values: CustomerInfoType,
    { resetForm, setErrors, setStatus, setSubmitting }: FormikHelpers<any>
  ) => {
    if (!executeRecaptcha) {
      console.error(
        "handleSubmitCreditCard() was called even though executeRecaptcha is not yet available"
      );
      return;
    }

    try {
      const endpoint =
        paymentGateway === "bpoint"
          ? "/api/payment-gateway-bpoint/create-auth-key"
          : "/api/payment-gateway-sawfish/init";

      const { data } = await axios.post(endpoint, {
        ...values,
        "g-recaptcha-response": await executeRecaptcha(
          "CustomerInfoForm_handleSubmitCreditCard"
        ),
      });
      const { AuthKey, TestMode, TestModeReason, transaction_uuid } = data;

      setValues((prevValue) => ({
        ...prevValue,
        ...values,
        authKey: AuthKey,
        testMode: TestMode,
        testModeReason: TestModeReason,
        transaction_uuid,
      }));

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);

      navigate("/payment");
    } catch (error: any) {
      let msg = "Something went wrong. Please try a again.";
      if (typeof error === "string") msg = error;
      else if (error?.message) msg = error?.message;

      setStatus({ sent: false });
      setErrors({ submit: msg });
      setSubmitting(false);
    }
  };

  const handleSubmitPreviousOrderUpdate = async (values: CustomerInfoType) => {
    if (!executeRecaptcha) {
      console.error(
        "handleSubmitPreviousOrderUpdate() was called even though executeRecaptcha is not yet available"
      );
      return;
    }

    await acceptOrderFromPrev({
      ...values,
      "g-recaptcha-response": await executeRecaptcha(
        "CustomerInfoForm_handleSubmitPreviousOrderUpdate"
      ),
    });

    navigate("/payment-result-previous-order-update", {
      state: {
        order_uuid: values.order_uuid,
        transaction_for: values.transaction_for,
        company_image: values.company_image,
        company_uuid: values.company_uuid,
        isCompanyOneSolomons: values.isCompanyOneSolomons,
      },
      replace: true,
    });
  };

  const FormContent = isCompanyOneSolomons
    ? OneSolomonCustomerInfoFormContent
    : DefaultCustomerInfoFormContent;

  return (
    <>
      {isCompanyOneSolomons && <OneSolomonPageBackground />}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          status,
          setFieldValue,
        }) => (
          <FormContent
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            touched={touched}
            values={values}
            status={status}
            setFieldValue={setFieldValue}
            transactionForDeposit={transactionForDeposit}
            isCompanyOneSolomons={isCompanyOneSolomons}
            initialValuesFromMain={initialValuesFromMain}
          />
        )}
      </Formik>
    </>
  );
};

export default CustomerInfoForm;
